import { workbenchApi } from "../src/features/api/workbenchApi.mjs";
import { generationStages } from "../src/features/ai/generationStages.mjs";
import { articleFromStreamText } from "../src/features/ai/streamingArticle.mjs";
import {
  buildPublishReminders,
  ensureArticleImageSuggestions
} from "../src/features/editor/articleEnhancements.mjs";
import { documentToArticle, resolveArticleDocument } from "../src/features/editor/articleDocument.mjs";
import { scrollToLatestContent } from "../src/features/editor/autoScroll.mjs";
import { createAiRewriteController } from "../src/features/editor/aiRewriteController.mjs";
import { createBlockCommandServices, writeBlockToClipboard } from "../src/features/editor/blockClipboard.mjs";
import { createContextualEditorUi } from "../src/features/editor/contextualEditorUi.mjs";
import { computeFloatingPosition } from "../src/features/editor/editorContext.mjs";
import { shouldDestroyEditorOnPageHide } from "../src/features/editor/editorPageLifecycle.mjs";
import { renderGenerationProgress } from "../src/features/editor/generationProgress.mjs";
import { sanitizeClipboardHtml } from "../src/features/editor/htmlSanitizer.mjs";
import { filterSlashCommands } from "../src/features/editor/slashCommands.mjs";
import { createSlashMenuController } from "../src/features/editor/slashMenuController.mjs";
import { createTiptapEditor } from "../src/features/editor/tiptapEditor.mjs";
import { articleToMarkdown, articleToPlainText, articleToWechatHtml, escapeHtml } from "../src/features/export/exportSerializers.mjs";
import { articleToWechatPublishHtml, wrapClipboardHtml } from "../src/features/export/wechatPublishHtml.mjs";
import { createProjectSaveQueue } from "../src/features/projects/projectSaveQueue.mjs";
import { createProjectSaveTimers } from "../src/features/projects/projectSaveTimers.mjs";

const AUTO_SAVE_DELAY_MS = 500;

const sourcePresets = {
  topic: {
    label: "当前主题",
    value: "",
    hint: "适合从一个选题快速生成草稿",
    limit: 80
  },
  material: {
    label: "素材片段",
    value: "",
    hint: "将优先使用素材中的核心观点",
    limit: 20000
  },
  link: {
    label: "公开网页链接",
    value: "",
    hint: "读取失败时可切换为手动粘贴正文",
    limit: null
  }
};

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => Array.from(document.querySelectorAll(selector));
const UNTITLED_ARTICLE = "未命名文章";
const displayArticleTitle = (value) => String(value || "").trim() || UNTITLED_ARTICLE;

const state = {
  projects: [],
  currentProjectId: null,
  currentArticle: null,
  currentTemplate: "fresh",
  selectedSelection: null,
  selectedText: "",
  exportFormat: "html",
  currentReview: null,
  lastSavedAt: null,
  session: null,
  authMode: "login"
};

const appShell = $("#appShell");
const documentPanel = $(".document");
const paper = $("#paper");
const toast = $("#toast");
let contextualUi = null;
let slashUi = null;
let aiRewriteUi = null;
let editor = null;
let editorUiDestroyed = false;
editor = createTiptapEditor({
  element: paper,
  content: { type: "doc", content: [] },
  onChange: handleEditorChange,
  onTitleFromPaste: handleTitleFromMarkdownPaste,
  transformPastedHTML: sanitizeClipboardHtml,
  onContextChange(snapshot) {
    handleEditorContextSnapshot(snapshot);
  },
  onKeyDown(event) {
    return slashUi?.handleKeyDown(event) ?? false;
  }
});

aiRewriteUi = createAiRewriteController({
  modal: $("#aiModal"),
  preview: $("#selectedTextPreview"),
  suggestion: $("#aiSuggestion"),
  getInstruction: () => $("#aiPrompt").value,
  rewriteSelection: (input) => workbenchApi.rewriteSelection(input),
  replaceSelection: (suggestion, selection) => editor.replaceSelection(suggestion, selection),
  onOpen: closeContextualSurfaces,
  onApplied() {
    state.selectedSelection = null;
    state.selectedText = "";
  },
  onToast: showToast
});

contextualUi = createContextualEditorUi({
  editor,
  elements: {
    blockTypeTrigger: $("#blockTypeTrigger"),
    blockMenu: $("#blockMenu"),
    selectionToolbar: $("#selectionToolbar"),
    selectionToolbarCommands: $("#selectionToolbarCommands"),
    selectionToolbarPanels: $("#selectionToolbarPanels"),
    slashMenu: $("#slashMenu"),
    aiBubble: $("#aiBubble")
  },
  services: createBlockCommandServices({ editor, writeClipboard: writeBlockToClipboard }),
  onAi(selection) {
    state.selectedText = selection.text;
    state.selectedSelection = selection;
    void openAiModal();
  },
  onCloseSlash: () => slashUi?.close(),
  readContextOnResize() {
    const snapshot = editor?.getContextSnapshot();
    syncSlashContext(snapshot);
    return snapshot;
  },
  onToast: showToast
});

function placeSlashMenu(element, caretRect) {
  const position = computeFloatingPosition({
    anchor: caretRect,
    surface: element.getBoundingClientRect(),
    viewport: document.documentElement.getBoundingClientRect()
  });
  if (!position) return;
  element.style.left = `${position.left}px`;
  element.style.top = `${position.top}px`;
  element.dataset.placement = position.placement;
}

slashUi = createSlashMenuController({
  element: $("#slashMenu"),
  ariaOwner: paper.querySelector('[contenteditable="true"]'),
  viewport: () => ({ left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight }),
  execute: (id, range) => editor.runSlashCommand(id, range),
  onPosition: placeSlashMenu
});
handleEditorContextSnapshot(editor.getContextSnapshot());
const projectSaveQueue = createProjectSaveQueue();
const projectSaveTimers = createProjectSaveTimers();

function syncSlashContext(snapshot) {
  let slash = null;
  try {
    slash = snapshot?.slash;
  } catch {
    slashUi?.close();
    return;
  }

  if (!slash || !editor) {
    slashUi?.close();
    return;
  }

  try {
    slashUi?.update({
      ...slash,
      commands: filterSlashCommands(editor.getCommandsForContext("slash"), slash.query)
    });
  } catch {
    slashUi?.close();
  }
}

function handleEditorContextSnapshot(snapshot) {
  contextualUi?.update(snapshot);
  if (editor) syncToolbarState();
  syncSlashContext(snapshot);
}

function parseSavedTimestamp(value) {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}T/.test(String(value))) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatLastSavedClock(date) {
  return date.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
}

function formatProjectListUpdatedAt(date) {
  const now = new Date();
  const time = date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });
  if (date.toDateString() === now.toDateString()) return `今天 ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `昨天 ${time}`;
  return date.toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Shanghai"
  });
}

function parseProjectDate(dateStr) {
  if (!dateStr) return new Date();
  const d = dateStr.endsWith("Z") || dateStr.includes("+") ? new Date(dateStr) : new Date(dateStr + "Z");
  return isNaN(d.getTime()) ? new Date() : d;
}

function updateSaveStateUI(status) {
  const el = $("#saveState");
  if (!el) return;

  if (status === "pending") {
    el.textContent = "● 待保存";
    return;
  }
  if (status === "saving") {
    el.textContent = "● 正在保存…";
    return;
  }
  if (status === "streaming") {
    el.textContent = "● 正在流式生成";
    return;
  }
  if (status === "failed") {
    el.textContent = state.lastSavedAt
      ? `● 保存失败 · 上次 ${formatLastSavedClock(state.lastSavedAt)}`
      : "● 保存失败";
    return;
  }
  if (status === "generate-failed") {
    el.textContent = "● 生成失败";
    return;
  }
  if (state.lastSavedAt) {
    el.textContent = `● 最后保存 ${formatLastSavedClock(state.lastSavedAt)} · 自动保存已开启`;
    return;
  }
  el.textContent = "● 尚未保存 · 自动保存已开启";
}

function markSavedAt(timestamp, projectId = state.currentProjectId) {
  const date = parseSavedTimestamp(timestamp) || new Date();
  const project = state.projects.find((item) => item.id === projectId);
  if (project) {
    project.updatedAt = formatProjectListUpdatedAt(date);
  }
  if (projectId === state.currentProjectId) {
    state.lastSavedAt = date;
    updateSaveStateUI("saved");
  }
  renderProjects();
}

function syncLastSavedFromProject(project) {
  state.lastSavedAt = parseSavedTimestamp(project?.updatedAt);
  updateSaveStateUI("idle");
}

const reviewStages = [
  {
    id: "read",
    label: "读取正文",
    activeLabel: "正在读取正文",
    description: "提取标题、摘要、分节和关键表达。"
  },
  {
    id: "facts",
    label: "核查风险",
    activeLabel: "正在核查风险",
    description: "检查事实依据、逻辑跳跃、夸大表达和来源缺口。"
  },
  {
    id: "structure",
    label: "检查结构",
    activeLabel: "正在检查结构",
    description: "评估开头吸引力、段落衔接、信息层级和结尾行动点。"
  },
  {
    id: "advice",
    label: "生成建议",
    activeLabel: "正在生成建议",
    description: "整理可执行的修改建议，并计算内容检查得分。"
  }
];

async function init() {
  bindEvents();
  await loadSession();
  await loadInitialProjects();
  renderAll();
}

async function loadSession() {
  if (workbenchApi.directFileMode) return;
  try {
    state.session = await workbenchApi.getSession();
  } catch {
    state.session = null;
  }
  renderAccountInfo();
}

async function loadInitialProjects() {
  if (!workbenchApi.directFileMode && !state.session) {
    state.projects = [];
    state.currentProjectId = null;
    state.currentArticle = createEmptyArticle("请先登录");
    showAuthModal("login");
    return;
  }
  try {
    state.projects = await workbenchApi.listProjects();
    if (state.projects.length === 0) {
      state.currentArticle = createEmptyArticle("暂无文章");
      return;
    }
    state.currentProjectId = state.projects[0].id;
    state.currentArticle = state.projects[0].article;
    syncLastSavedFromProject(state.projects[0]);
  } catch (error) {
    state.projects = [];
    state.currentProjectId = null;
    state.currentArticle = createEmptyArticle("服务端暂不可用");
    showToast(`服务端暂不可用，请稍后刷新重试`);
  }
}

function renderAll() {
  renderProjects();
  renderArticle();
  renderPreview();
  renderAssistant();
  updateProjectChrome();
}

function createEmptyArticle(title) {
  return {
    id: `article-${Date.now()}`,
    title,
    titleCandidates: [title],
    summary: "暂无文章",
    sections: [
      {
        id: "sec-empty",
        heading: "等待内容",
        paragraphs: ["请在左侧输入主题，开始生成第一篇文章。"]
      }
    ],
    imageSuggestions: [],
    quoteCandidates: [],
    sourceWarnings: []
  };
}

function renderProjects() {
  const list = $("#projectList");
  list.innerHTML = "";
  for (const project of state.projects) {
    const button = document.createElement("div");
    button.className = `project-item${project.id === state.currentProjectId ? " active" : ""}`;
    button.dataset.projectId = project.id;
    button.innerHTML = `
      <div class="project-item-text">
        <strong>${escapeHtml(displayArticleTitle(project.article?.title ?? project.title))}</strong>
        <span>${formatProjectListUpdatedAt(parseProjectDate(project.updatedAt))}</span>
      </div>
      <button class="delete-btn" data-delete="${project.id}" type="button" title="删除">×</button>
    `;
    list.append(button);
  }
}

function renderArticle() {
  closeContextualSurfaces();
  const article = state.currentArticle;
  const resolved = resolveArticleDocument(article);
  state.currentArticle = { ...article, title: resolved.title, contentJson: resolved.document };
  const project = state.projects.find((item) => item.id === state.currentProjectId);
  if (project) {
    project.article = state.currentArticle;
    project.title = state.currentArticle.title;
    renderProjects();
  }
  $("#articleTitle").value = resolved.title;
  editor.setDocument(resolved.document);
  if (resolved.warning) showToast("已忽略损坏的编辑器内容，并从文章正文恢复");
  updateWordCount();
}

function createBlankArticle() {
  const timestamp = Date.now();
  return {
    id: `article-${timestamp}`,
    title: "",
    titleCandidates: [],
    summary: "",
    sections: [{ id: `section-${timestamp}`, heading: "", paragraphs: [""] }],
    imageSuggestions: [],
    quoteCandidates: [],
    sourceWarnings: [],
    contentJson: { type: "doc", content: [{ type: "paragraph", content: [] }] }
  };
}

function renderProgressStage({ activeStageId, sourceLabel, taskLabel, stages, title }) {
  closeContextualSurfaces();
  $("#progressModalTitle").textContent = title;
  $("#progressModalBody").innerHTML = renderGenerationProgress({
    activeStageId,
    sourceLabel,
    taskLabel,
    stages
  });
  const modal = $("#progressModal");
  if (!modal.open) {
    modal.showModal();
  }
}

function closeProgressModal() {
  const modal = $("#progressModal");
  if (modal.open) {
    modal.close();
  }
}

function renderPreview() {
  const article = readCurrentArticle();
  $("#wechatPreview").innerHTML = articleToWechatPublishHtml(article, {
    templateId: state.currentTemplate
  });
}

function renderAssistant(review = state.currentReview) {
  const issues = getAssistantIssues(review);
  const issueListEl = $("#issueList");
  if (issueListEl) {
    issueListEl.innerHTML = issues.map((issue) => `
      <div class="issue-row">
        <div class="assistant-row-copy">
          <strong>${escapeHtml(issue.title)}</strong>
          <span>${escapeHtml(issue.description)}</span>
          ${issue.suggestion ? `<small>${escapeHtml(issue.suggestion)}</small>` : ""}
        </div>
      </div>
    `).join("");
  }
  const imageListEl = $("#imageList");
  if (imageListEl) {
    imageListEl.innerHTML = (state.currentArticle.imageSuggestions || []).map((image) => `
      <div class="image-row">
        <div class="assistant-row-copy">
          <strong>${escapeHtml(image.title)}</strong>
          <span>${escapeHtml(image.placement)}：${escapeHtml(image.description)}</span>
        </div>
        <button class="assistant-action" data-action="apply-image" data-image-id="${escapeHtml(image.id)}" type="button">插入</button>
      </div>
    `).join("") || `<div class="ready-row">生成文章后展示配图建议。</div>`;
  }
  const publishReminderListEl = $("#publishReminderList");
  if (publishReminderListEl) {
    publishReminderListEl.innerHTML = buildPublishReminders({
      article: state.currentArticle,
      review
    }).map((reminder) => `<div class="ready-row">${escapeHtml(reminder)}</div>`).join("");
  }
}

function getAssistantIssues(review = state.currentReview) {
  return review?.issues || [
    { id: "issue-fact", type: "fact", severity: "medium", title: "事实待核", description: "涉及平台规则或增长数据时，需要补充来源。", targetSectionId: "sec-beyond-draft" },
    { id: "issue-logic", type: "logic", severity: "low", title: "结构建议", description: "可以补一句为什么公众号场景更需要结构化。", targetSectionId: "sec-process" },
    { id: "issue-style", type: "style", severity: "high", title: "表达风险", description: "增长表述较笼统，建议说明目标。", targetSectionId: "sec-system" }
  ];
}

function updateProjectChrome() {
  const account = state.session?.user;
  const avatar = $("#accountAvatar");
  const nameEl = $("#accountName");
  if (account && avatar && nameEl) {
    avatar.textContent = (account.displayName || account.email || "U").slice(0, 1).toUpperCase();
    nameEl.textContent = account.displayName || account.email || "体验模式";
  } else if (avatar && nameEl) {
    avatar.textContent = "游";
    nameEl.textContent = "体验模式";
  }
}

function bindEvents() {
  $("#generateArticle").addEventListener("click", handleGenerate);
  $("#projectList").addEventListener("click", handleProjectClick);
  $("#createBlankArticle").addEventListener("click", handleCreateBlankArticle);
  $("#paper").addEventListener(
    "blur",
    () => {
      persistAndQueueSave();
      void flushProjectSave(state.currentProjectId);
    },
    true
  );
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    persistAndQueueSave();
    void flushProjectSave(state.currentProjectId);
  });
  $("#editorToolbar").addEventListener("click", handleToolbarClick);
  $("#articleTitle").addEventListener("input", handleArticleTitleInput);
  $("#openExport").addEventListener("click", openExport);
  $("#copyExport").addEventListener("click", copyExport);

  $("#hideSide").addEventListener("click", enterImmersiveMode);
  $("#restoreSide").addEventListener("click", () => toggleShell("side-hidden", false, "已显示左侧边栏"));
  $("#hidePreview").addEventListener("click", () => toggleShell("preview-hidden", true, "已隐藏公众号预览"));
  $("#restorePreview").addEventListener("click", () => toggleShell("preview-hidden", false, "已显示公众号预览"));

  $$(".device-switch button").forEach((button) => button.addEventListener("click", () => switchDevice(button)));
  $$(".template-tab").forEach((button) => button.addEventListener("click", () => switchTemplate(button)));
  $("#accountTrigger").addEventListener("click", () => {
    if (state.session?.user) {
      $("#accountMenu").classList.toggle("open");
    } else {
      showAuthModal("login");
    }
  });
  $("#logoutAction").addEventListener("click", handleLogout);
  $("#openUserInfo")?.addEventListener("click", () => { $("#accountMenu").classList.remove("open"); openSlidePanel("userinfo"); });
  $("#openPreferences")?.addEventListener("click", () => { $("#accountMenu").classList.remove("open"); openSlidePanel("preferences"); });
  $("#openQuota")?.addEventListener("click", () => { $("#accountMenu").classList.remove("open"); openSlidePanel("quota"); });
  $("#slidePanelClose")?.addEventListener("click", closeSlidePanel);
  $("#slidePanelBack")?.addEventListener("click", closeSlidePanel);
  $("#slidePanelOverlay")?.addEventListener("click", closeSlidePanel);
  $$(".slide-panel-tab").forEach((tab) => tab.addEventListener("click", () => switchSlideTab(tab.dataset.tab)));
  $("#savePreferences")?.addEventListener("click", handleSavePreferences);
  $("#redeemCodeBtn")?.addEventListener("click", handleRedeemCode);
  $("#redeemCodeInput")?.addEventListener("keydown", (e) => { if (e.key === "Enter") handleRedeemCode(); });
  $$(".history-tab").forEach((tab) => tab.addEventListener("click", () => {
    quotaHistoryTab = tab.dataset.history;
    $$(".history-tab").forEach((t) => t.classList.toggle("active", t === tab));
    renderQuotaHistory();
  }));
  $("#authForm")?.addEventListener("submit", handleAuthSubmit);
  $("#toggleAuthMode")?.addEventListener("click", () => showAuthModal(state.authMode === "login" ? "register" : "login"));
  $("#refreshAi").addEventListener("click", refreshAiSuggestion);
  $("#applyAi").addEventListener("click", applyAiSuggestion);
  $("#aiModal").addEventListener("close", () => aiRewriteUi?.invalidate());
  $("#imageList")?.addEventListener("click", handleImageListClick);
  $$(".export-option").forEach((button) => button.addEventListener("click", () => selectExport(button)));
  $$(".package-item").forEach((item) => item.addEventListener("click", () => {
    closeContextualSurfaces();
    $("#rechargeModal").showModal();
  }));
  $("#openRechargeModal")?.addEventListener("click", () => {
    closeContextualSurfaces();
    $("#rechargeModal").showModal();
  });
  document.addEventListener("keydown", handleGlobalShortcuts);
  window.addEventListener("pagehide", handleEditorPageHide);
  bindResizeHandle();
  initComboboxes();
}

function handleToolbarClick(event) {
  const button = event.target.closest("[data-command]");
  if (!button || !$("#editorToolbar").contains(button)) return;
  editor.runCommand(button.dataset.command);
  syncToolbarState();
}

function syncToolbarState() {
  $$("#editorToolbar [data-command]").forEach((button) => {
    const command = button.dataset.command;
    const active = editor.isActive(command);
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
    button.disabled = !editor.canRunCommand(command);
  });
}

function initComboboxes() {
  const ids = ["prefAudience", "prefTone", "prefLength", "prefPurpose", "prefTemplate"];
  const datalistIds = { prefAudience: "audienceList", prefTone: "toneList", prefLength: "lengthList", prefPurpose: "purposeList", prefTemplate: "templateList" };
  ids.forEach((id) => {
    const input = $(`#${id}`);
    const dropdown = $(`#${id}Dropdown`);
    const arrow = $(`.combobox-arrow[data-for="${id}"]`);
    if (!input || !dropdown || !arrow) return;

    const datalist = $(`#${datalistIds[id]}`);
    const opts = datalist ? Array.from(datalist.options).map((o) => o.value) : [];

    function openDropdown() {
      if (!opts.length) return;
      dropdown.innerHTML = opts
        .map((v) => `<div class="combobox-option${input.value === v ? " selected" : ""}" data-value="${v.replace(/"/g, "&quot;")}">${v}</div>`)
        .join("");
      dropdown.classList.add("open");
    }

    function closeDropdown() {
      dropdown.classList.remove("open");
    }

    arrow.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dropdown.classList.contains("open")) {
        closeDropdown();
      } else {
        closeAllComboboxes();
        openDropdown();
      }
    });

    input.addEventListener("focus", () => closeAllComboboxes());

    input.addEventListener("input", () => {
      if (dropdown.classList.contains("open")) {
        const match = opts.find((v) => v === input.value);
        if (!match) closeDropdown();
      }
    });

    dropdown.addEventListener("click", (e) => {
      const opt = e.target.closest(".combobox-option");
      if (!opt) return;
      input.value = opt.dataset.value;
      closeDropdown();
      input.dispatchEvent(new Event("change"));
    });
  });

  document.addEventListener("click", (e) => {
    if (!e.target.closest(".combobox-wrap")) closeAllComboboxes();
  });
}

function closeAllComboboxes() {
  $$(".combobox-dropdown").forEach((el) => el.classList.remove("open"));
}

function bindResizeHandle() {
  const handle = $("#resizeHandle");
  if (!handle) return;
  let dragging = false;
  handle.addEventListener("mousedown", (e) => {
    dragging = true;
    handle.classList.add("dragging");
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  });
  document.addEventListener("mousemove", (e) => {
    if (!dragging) return;
    const app = $(".app");
    const rect = app.getBoundingClientRect();
    const newWidth = rect.right - e.clientX;
    const minW = 240;
    const maxW = 680;
    if (newWidth >= minW && newWidth <= maxW) {
      app.style.setProperty("--preview-width", `${newWidth}px`);
    }
  });
  document.addEventListener("mouseup", () => {
    if (!dragging) return;
    dragging = false;
    handle.classList.remove("dragging");
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  });
}

function openSlidePanel(tab = "userinfo") {
  closeContextualSurfaces();
  const panel = $("#slidePanel");
  const overlay = $("#slidePanelOverlay");
  if (!panel) return;
  panel.classList.add("open");
  overlay?.classList.add("open");
  switchSlideTab(tab);
  if (tab === "userinfo") {
    loadUserInfo();
  } else {
    loadPreferences();
  }
}

function closeSlidePanel() {
  const panel = $("#slidePanel");
  const overlay = $("#slidePanelOverlay");
  panel?.classList.remove("open");
  overlay?.classList.remove("open");
}

function switchSlideTab(tab) {
  $$(".slide-panel-tab").forEach((t) => t.classList.toggle("active", t.dataset.tab === tab));
  $("#userinfoContent").style.display = tab === "userinfo" ? "block" : "none";
  $("#preferencesContent").style.display = tab === "preferences" ? "block" : "none";
  $("#quotaContent").style.display = tab === "quota" ? "block" : "none";
  $("#slidePanelTitle").textContent = tab === "userinfo" ? "个人信息" : tab === "preferences" ? "偏好设置" : "创作额度";
  if (tab === "quota") loadQuota();
}

function loadUserInfo() {
  const user = state.session?.user;
  const token = state.session?.tokenAccount;
  if (user) {
    $("#infoDisplayName").textContent = user.displayName || "-";
    $("#infoEmail").textContent = user.email || "-";
    $("#infoCreatedAt").textContent = user.createdAt ? new Date(user.createdAt).toLocaleDateString("zh-CN") : "-";
    $("#infoTokenBalance").textContent = token ? `${(token.remainingArticles ?? 0)} 篇` : "-";
  }
}

async function loadPreferences() {
  if (!state.session?.user) return;
  try {
    const prefs = await workbenchApi.getUserPreferences();
    state.userPreferences = prefs;
    $("#prefAudience").value = prefs.audience || "";
    $("#prefTone").value = prefs.tone || "";
    $("#prefLength").value = prefs.length === "short" ? "短篇 800-1200字" : prefs.length === "long" ? "长篇 2500-4000字" : "中等 1200-2500字";
    $("#prefPurpose").value = prefs.purpose || "";
    $("#prefTemplate").value = prefs.layoutTemplate === "professional" ? "专业简报" : prefs.layoutTemplate === "magazine" ? "轻杂志感" : "清爽阅读";
    $("#prefBrandDesc").value = prefs.brandDesc || "";
    $("#prefExpertiseAreas").value = prefs.expertiseAreas || "";
    $("#prefAudienceDesc").value = prefs.audienceDesc || "";
    $("#prefToneGuidelines").value = prefs.toneGuidelines || "";
    $("#prefRedLines").value = prefs.redLines || "";
    $("#prefCustomPrompt").value = prefs.customPrompt || "";
  } catch (e) {
    showToast("加载偏好设置失败");
  }
}

let quotaData = { account: {}, logs: [], redemptions: [] };
let quotaHistoryTab = "generation";

function renderQuotaHistory() {
  const list = $("#historyList");
  if (quotaHistoryTab === "generation") {
    if (quotaData.logs && quotaData.logs.length > 0) {
      list.innerHTML = quotaData.logs.map((log) => `
        <div class="history-item">
          <span class="history-title">${escapeHtml(log.articleTitle || "未命名文章")}</span>
          <span class="history-date">${new Date(log.createdAt).toLocaleDateString("zh-CN")}</span>
        </div>
      `).join("");
    } else {
      list.innerHTML = '<div class="history-empty">暂无消费记录</div>';
    }
  } else {
    if (quotaData.redemptions && quotaData.redemptions.length > 0) {
      list.innerHTML = quotaData.redemptions.map((r) => `
        <div class="history-item">
          <span class="history-title">兑换 ${r.articles} 篇</span>
          <span class="history-date">${new Date(r.redeemedAt).toLocaleDateString("zh-CN")}</span>
        </div>
      `).join("");
    } else {
      list.innerHTML = '<div class="history-empty">暂无兑换记录</div>';
    }
  }
}

async function loadQuota() {
  if (!state.session?.user) return;
  try {
    const data = await workbenchApi.getAccountInfo();
    quotaData = data;
    const { account } = data;
    const remaining = account.remainingArticles ?? 0;
    const limit = account.articlesLimit ?? 0;
    const used = account.articlesUsed ?? 0;
    $("#quotaRemaining").textContent = remaining;
    $("#quotaSub").textContent = `已使用 ${used} 篇 / 共 ${limit} 篇`;
    const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    $("#quotaBar").style.width = `${pct}%`;
    renderQuotaHistory();
  } catch (e) {
    showToast("加载额度信息失败");
  }
}

async function handleRedeemCode() {
  const code = $("#redeemCodeInput").value.trim();
  if (!code) return;
  try {
    const result = await workbenchApi.redeemCode(code);
    $("#redeemCodeInput").value = "";
    showToast(`兑换成功，获得 ${result.redemption.articles} 篇`);
    await loadQuota();
  } catch (e) {
    showToast(e.message || "兑换失败");
  }
}

async function handleSavePreferences() {
  const lengthMap = { "短篇 800-1200字": "short", "中等 1200-2500字": "medium", "长篇 2500-4000字": "long" };
  const templateMap = { "清爽阅读": "fresh", "专业简报": "professional", "轻杂志感": "magazine" };
  const prefs = {
    audience: $("#prefAudience").value,
    tone: $("#prefTone").value,
    length: lengthMap[$("#prefLength").value] || "medium",
    purpose: $("#prefPurpose").value,
    layoutTemplate: templateMap[$("#prefTemplate").value] || "fresh",
    brandDesc: $("#prefBrandDesc").value,
    expertiseAreas: $("#prefExpertiseAreas").value,
    audienceDesc: $("#prefAudienceDesc").value,
    toneGuidelines: $("#prefToneGuidelines").value,
    redLines: $("#prefRedLines").value,
    customPrompt: $("#prefCustomPrompt").value
  };
  try {
    state.userPreferences = await workbenchApi.updateUserPreferences(prefs);
    showToast("设置已保存");
  } catch (e) {
    showToast("保存失败");
  }
}

function showAuthModal(mode = "login") {
  closeContextualSurfaces();
  state.authMode = mode;
  $("#authTitle").textContent = mode === "login" ? "登录章法 AI" : "注册章法 AI";
  $("#submitAuth").textContent = mode === "login" ? "登录" : "注册";
  $("#toggleAuthMode").textContent = mode === "login" ? "切换到注册" : "返回登录";
  $("#displayNameField").style.display = mode === "login" ? "none" : "grid";
  $("#authError").textContent = "";
  const modal = $("#authModal");
  if (!modal.open) modal.showModal();
}

async function handleAuthSubmit(event) {
  event.preventDefault();
  const submit = $("#submitAuth");
  submit.disabled = true;
  $("#authError").textContent = "";
  try {
    const input = {
      email: $("#authEmail").value,
      password: $("#authPassword").value,
      displayName: $("#authDisplayName").value
    };
    state.session = state.authMode === "login"
      ? await workbenchApi.login(input)
      : await workbenchApi.register(input);
    $("#authModal").close();
    renderAccountInfo();
    await loadInitialProjects();
    renderAll();
    showToast(state.authMode === "login" ? "登录成功" : "注册成功");
  } catch (error) {
    $("#authError").textContent = error.message;
  } finally {
    submit.disabled = false;
  }
}

function renderAccountInfo() {
  const avatar = $("#accountAvatar");
  const nameEl = $("#accountName");
  const tokenEl = $("#accountToken");
  if (!avatar || !nameEl) return;
  const user = state.session?.user;
  const tokenAccount = state.session?.tokenAccount;
  if (user) {
    avatar.textContent = (user.displayName || user.email || "U").slice(0, 1).toUpperCase();
    nameEl.textContent = user.displayName || user.email || "体验模式";
    if (tokenEl) {
      tokenEl.textContent = tokenAccount ? `${(tokenAccount.remainingTokens ?? 0).toLocaleString()}` : "";
    }
  } else {
    avatar.textContent = "游";
    nameEl.textContent = "体验模式";
    if (tokenEl) tokenEl.textContent = "";
  }
}

async function handleLogout() {
  await workbenchApi.logout();
  state.session = null;
  state.projects = [];
  state.currentProjectId = null;
  state.currentArticle = createEmptyArticle("请先登录");
  $("#accountMenu").classList.remove("open");
  renderAccountInfo();
  renderAll();
  showAuthModal("login");
  showToast("已退出登录");
}

async function handleGenerate() {
  if (!state.session?.user) {
    showAuthModal("login");
    return;
  }
  const button = $("#generateArticle");
  button.disabled = true;
  button.textContent = "流式生成中";
  editor.setEditable(false);
  let hasStartedWriting = false;
  try {
    const input = {
      sourceText: $("#sourceInput").value,
      tone: $("#toneSelect").value,
      length: $("#lengthSelect").value
    };
    let streamText = "";
    let finalArticle = null;

    updateSaveStateUI("streaming");
    state.currentReview = null;
    showToast("文章开始流式生成");

    for await (const event of workbenchApi.generateArticleStream(input)) {
      if (event.type === "chunk") {
        if (!hasStartedWriting) {
          hasStartedWriting = true;
          state.currentProjectId = null;
        }
        streamText += event.text;
        state.currentArticle = articleFromStreamText(streamText, { fallbackTitle: "正在生成文章" });
        renderArticle();
        renderPreview();
        updateProjectChrome();
        scrollToLatestContent(documentPanel);
      }
      if (event.type === "article") {
        finalArticle = event.article;
      }
    }

    const article = finalArticle || articleFromStreamText(streamText, { fallbackTitle: "未命名公众号文章" });
    const project = await workbenchApi.createProject({
      article,
      inputType: "主题"
    });
    state.projects = [project, ...state.projects.filter((item) => item.id !== project.id)];
    state.currentProjectId = project.id;
    state.currentArticle = project.article;
    renderAll();
    scrollToLatestContent(documentPanel);
    markSavedAt(project.updatedAt);
    showToast("文章草稿已流式生成并保存");
  } catch (error) {
    updateSaveStateUI("generate-failed");
    showToast(`生成失败：${error.message}`);
  } finally {
    button.disabled = false;
    button.textContent = "生成文章";
    editor.setEditable(true);
  }
}

function startGenerationProgress(sourceLabel) {
  let index = 0;
  let stopped = false;
  const lastPreWritingIndex = Math.max(0, generationStages.length - 2);

  const show = () => {
    if (stopped) return;
    renderProgressStage({
      activeStageId: generationStages[index].id,
      sourceLabel,
      taskLabel: "生成任务",
      stages: generationStages.slice(0, lastPreWritingIndex + 1),
      title: "生成进度"
    });
  };

  show();
  const timer = setInterval(() => {
    if (stopped) return;
    index = Math.min(index + 1, lastPreWritingIndex);
    show();
  }, 900);

  return {
    startWriting() {
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      closeProgressModal();
    },
    stop() {
      stopped = true;
      clearInterval(timer);
      closeProgressModal();
    }
  };
}

function startReviewProgress() {
  let index = 0;
  let stopped = false;

  const show = () => {
    if (stopped) return;
    renderProgressStage({
      activeStageId: reviewStages[index].id,
      sourceLabel: "内容",
      taskLabel: "检查任务",
      stages: reviewStages,
      title: "检查进度"
    });
  };

  show();
  const timer = setInterval(() => {
    if (stopped) return;
    index = Math.min(index + 1, reviewStages.length - 1);
    show();
  }, 900);

  return {
    stop() {
      stopped = true;
      clearInterval(timer);
      closeProgressModal();
    }
  };
}

function persistAndQueueSave() {
  const article = persistCurrentArticleFromEditor();
  if (!article || !state.currentProjectId) return article;
  queueProjectSave({ title: article.title, article });
  return article;
}

function handleProjectClick(event) {
  const deleteBtn = event.target.closest(".delete-btn");
  if (deleteBtn) {
    const id = deleteBtn.dataset.delete;
    const project = state.projects.find((p) => p.id === id);
    if (!project) return;
    if (!confirm(`确定删除「${project.title}」？`)) return;
    projectSaveTimers.cancel(id);
    projectSaveQueue.discard(id);
    workbenchApi.deleteProject(id).catch(() => {});
    state.projects = state.projects.filter((p) => p.id !== id);
    if (state.currentProjectId === id) {
      state.currentProjectId = state.projects[0]?.id || null;
      state.currentArticle = state.projects[0]?.article || createEmptyArticle("暂无文章");
    }
    renderAll();
    showToast("已删除");
    return;
  }
  const item = event.target.closest(".project-item");
  if (!item) return;
  const project = state.projects.find((entry) => entry.id === item.dataset.projectId);
  if (!project) return;
  persistAndQueueSave();
  void flushProjectSave(state.currentProjectId);
  if (project.id === state.currentProjectId) return;
  state.currentProjectId = project.id;
  state.currentArticle = project.article;
  syncLastSavedFromProject(project);
  renderAll();
  showToast(`已打开文章：${project.title}`);
}

function persistCurrentArticleFromEditor(document = editor.getDocument()) {
  if (!state.currentArticle) return null;
  const article = documentToArticle(document, state.currentArticle);
  state.currentArticle = article;
  const project = state.projects.find((item) => item.id === state.currentProjectId);
  if (project) {
    project.title = article.title;
    project.article = article;
  }
  return article;
}

function handleEditorChange(document) {
  const article = persistCurrentArticleFromEditor(document);
  if (!article) return;
  updateWordCount();
  renderPreview();
  queueProjectSave({ title: article.title, article });
  updateSaveStateUI("pending");
  syncToolbarState();
}

async function handleManualSave() {
  const button = $("#saveProject");
  const article = persistAndQueueSave();
  if (!article || !state.currentProjectId) {
    showToast("当前没有可保存的文章");
    return;
  }

  projectSaveTimers.cancel(state.currentProjectId);
  button.disabled = true;
  updateSaveStateUI("saving");
  try {
    await flushProjectSave(state.currentProjectId);
    showToast("已手动保存");
  } finally {
    button.disabled = false;
  }
}

function readCurrentArticle() {
  return state.currentArticle
    ? documentToArticle(editor.getDocument(), state.currentArticle)
    : createEmptyArticle("未命名文章");
}

async function handleCreateBlankArticle() {
  const button = $("#createBlankArticle");
  if (button.disabled) return;
  button.disabled = true;
  try {
    const created = await workbenchApi.createProject({ article: createBlankArticle(), inputType: "空白" });
    const project = { ...created, title: created.article?.title || "" };
    state.projects = [project, ...state.projects.filter((item) => item.id !== project.id)];
    state.currentProjectId = project.id;
    state.currentArticle = project.article;
    state.currentReview = null;
    syncLastSavedFromProject(project);
    renderAll();
    $("#articleTitle").focus();
  } catch (error) {
    showToast(`新建文章失败：${error.message}`);
  } finally {
    button.disabled = false;
  }
}

function applyArticleTitle(value, { queueSave = true } = {}) {
  if (!state.currentArticle) return () => {};
  const previousTitle = state.currentArticle.title || "";
  const title = String(value || "").replace(/[\r\n]+/g, " ").slice(0, 120);
  $("#articleTitle").value = title;
  state.currentArticle = { ...state.currentArticle, title: title.trim() };
  const project = state.projects.find((item) => item.id === state.currentProjectId);
  if (project) {
    project.title = state.currentArticle.title;
    project.article = state.currentArticle;
  }
  renderProjects();
  renderPreview();
  updateProjectChrome();
  updateWordCount();
  if (queueSave && state.currentProjectId) {
    queueProjectSave({ title: state.currentArticle.title, article: state.currentArticle });
    updateSaveStateUI("pending");
  }
  return () => applyArticleTitle(previousTitle, { queueSave: false });
}

function handleArticleTitleInput(event) {
  applyArticleTitle(event.target.value);
}

function handleTitleFromMarkdownPaste(title) {
  return applyArticleTitle(title, { queueSave: false });
}

function queueProjectSave(patch, projectId = state.currentProjectId) {
  if (!projectId) return;
  projectSaveQueue.enqueue(projectId, patch);
  projectSaveTimers.cancel(projectId);
  projectSaveTimers.schedule(projectId, () => {
    void flushProjectSave(projectId);
  }, AUTO_SAVE_DELAY_MS);
}

async function flushProjectSave(projectId) {
  if (!projectSaveQueue.has(projectId)) return;
  projectSaveTimers.cancel(projectId);
  try {
    await projectSaveQueue.flush(projectId, async (entry) => {
      const updated = await workbenchApi.updateProject(entry.projectId, entry.patch);
      markSavedAt(updated?.updatedAt, entry.projectId);
    });
  } catch (error) {
    if (!state.projects.some((project) => project.id === projectId)) {
      projectSaveQueue.discard(projectId);
      return;
    }
    if (projectId === state.currentProjectId) updateSaveStateUI("failed");
    showToast(`保存失败：${error.message}`);
    projectSaveTimers.schedule(projectId, () => void flushProjectSave(projectId), AUTO_SAVE_DELAY_MS);
  }
}

async function handleReview() {
  const button = $("#runCheck");
  button.disabled = true;
  button.textContent = "检查中";
  $("#scoreLabel").textContent = "正在检查内容...";
  const progressController = startReviewProgress();
  try {
    state.currentArticle = ensureArticleImageSuggestions(readCurrentArticle());
    const review = await workbenchApi.reviewArticle(state.currentArticle);
    state.currentReview = review;
    $("#scoreLabel").textContent = "内容检查得分";
    $("#scoreValue").textContent = `${review.score}/100`;
    $("#scoreMeter").style.width = `${review.score}%`;
    const project = state.projects.find((item) => item.id === state.currentProjectId);
    if (project) {
      project.score = review.score;
      project.article = state.currentArticle;
      const updated = await workbenchApi.updateProject(project.id, {
        score: review.score,
        article: state.currentArticle
      });
      markSavedAt(updated?.updatedAt);
    }
    renderAssistant(state.currentReview);
    showToast(`检查完成，发现 ${review.issues.length} 条待处理建议`);
  } catch (error) {
    showToast(`检查失败：${error.message}`);
  } finally {
    progressController.stop();
    button.disabled = false;
    button.textContent = "检查";
  }
}

function handleImageListClick(event) {
  const button = event.target.closest("[data-action=\"apply-image\"]");
  if (!button) return;
  handleApplyImageSuggestion(button.dataset.imageId);
}

function handleApplyImageSuggestion(imageId) {
  const image = (state.currentArticle.imageSuggestions || []).find((item) => item.id === imageId);
  if (!image) return;
  const inserted = editor.insertImagePlaceholder({
    title: image.title,
    placement: image.placement,
    description: image.description
  });
  showToast(inserted ? "已插入配图占位符" : "未能插入配图占位符，请重试");
}

function closeContextualSurfaces() {
  contextualUi?.close();
  slashUi?.close();
}

function destroyEditorUi() {
  if (editorUiDestroyed) return;
  editorUiDestroyed = true;
  aiRewriteUi?.invalidate();
  contextualUi?.destroy();
  slashUi?.destroy();
  editor?.destroy();
}

function handleEditorPageHide(event) {
  if (shouldDestroyEditorOnPageHide(event)) destroyEditorUi();
}

function openAiModal() {
  return aiRewriteUi?.open(state.selectedSelection);
}

function refreshAiSuggestion() {
  return aiRewriteUi?.refresh();
}

function applyAiSuggestion() {
  return aiRewriteUi?.apply();
}

function switchDevice(button) {
  $$(".device-switch button").forEach((item) => item.classList.remove("active"));
  button.classList.add("active");
  $("#previewCanvas").classList.toggle("desktop", button.dataset.device === "desktop");
  $("#previewCanvas").classList.toggle("mobile", button.dataset.device === "mobile");
}

function switchTemplate(button) {
  $$(".template-tab").forEach((item) => item.classList.remove("active"));
  button.classList.add("active");
  state.currentTemplate = button.dataset.template;
  renderPreview();
}

function openExport() {
  closeContextualSurfaces();
  updateExportContent();
  $("#exportModal").showModal();
}

function selectExport(button) {
  $$(".export-option").forEach((item) => item.classList.remove("active"));
  button.classList.add("active");
  state.exportFormat = button.dataset.format;
  updateExportContent();
}

function updateExportContent() {
  if (state.currentProjectId) {
    state.currentArticle = readCurrentArticle();
  }
  const options = {
    unresolvedWarnings: state.currentArticle.sourceWarnings,
    sources: [`主题：${state.currentArticle.title}`],
    templateId: state.currentTemplate
  };
  const content = state.exportFormat === "markdown"
    ? articleToMarkdown(state.currentArticle, options)
    : state.exportFormat === "html"
      ? articleToWechatHtml(state.currentArticle, options)
      : articleToPlainText(state.currentArticle);
  $("#exportContent").value = content;
}

async function copyExport() {
  updateExportContent();
  const content = $("#exportContent").value;

  if (state.exportFormat === "html") {
    try {
      const htmlBlob = new Blob([wrapClipboardHtml(content)], { type: "text/html" });
      const plainBlob = new Blob([articleToPlainText(state.currentArticle)], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": plainBlob
        })
      ]);
      showToast("已复制带样式正文，可直接粘贴到公众号编辑器");
      return;
    } catch {
      // fall through to textarea copy
    }
  }

  $("#exportContent").select();
  try {
    await navigator.clipboard.writeText(content);
    showToast("导出内容已复制");
  } catch {
    showToast("导出内容已选中，可手动复制");
  }
}

function toggleShell(className, enabled, message) {
  appShell.classList.toggle(className, enabled);
  showToast(message);
}

function handleGlobalShortcuts(event) {
  if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "s") {
    event.preventDefault();
    void handleManualSave();
    return;
  }
  if (!isImmersiveShortcut(event)) return;
  event.preventDefault();
  enterImmersiveMode();
}

function isImmersiveShortcut(event) {
  return event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "f" && !event.altKey && !event.metaKey;
}

function enterImmersiveMode() {
  appShell.classList.add("side-hidden", "preview-hidden");
  showToast("已进入沉浸式写作");
}

function updateWordCount() {
  const text = `${$("#articleTitle").value}${editor.getText()}`;
  $("#wordCount").textContent = `约 ${text.trim().length} 字`;
}

function showToast(text) {
  toast.textContent = text;
  toast.classList.add("show");
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => toast.classList.remove("show"), 1800);
}

init();
